import React, { useId } from "react";

import "./ui.css";

const getBreadcrumbDetails = (item) => {
  if (item && typeof item === "object" && !React.isValidElement(item)) {
    return item;
  }

  return { label: item };
};

/**
 * Shared, presentation-only heading for application pages.
 *
 * Breadcrumbs accept strings or objects shaped like
 * { label, href, onClick, current }. Actions and help are React nodes so
 * existing screens can keep ownership of navigation and business logic.
 */
const PageHeader = ({
  title,
  subtitle,
  eyebrow,
  breadcrumbs = [],
  actions,
  help,
  children,
  className = "",
  titleId,
}) => {
  const generatedId = useId().replace(/:/g, "");
  const resolvedTitleId = titleId || `rg-ui-page-title-${generatedId}`;
  const classes = ["rg-ui-page-header", className].filter(Boolean).join(" ");

  return (
    <header className={classes} aria-labelledby={resolvedTitleId}>
      <div className="rg-ui-page-header__content">
        {breadcrumbs.length > 0 && (
          <nav className="rg-ui-breadcrumbs" aria-label="Breadcrumb">
            <ol>
              {breadcrumbs.map((item, index) => {
                const details = getBreadcrumbDetails(item);
                const isCurrent =
                  typeof details.current === "boolean"
                    ? details.current
                    : index === breadcrumbs.length - 1;
                const key = details.key || `${String(details.label)}-${index}`;

                return (
                  <li key={key}>
                    {details.href && !isCurrent ? (
                      <a href={details.href} onClick={details.onClick}>
                        {details.label}
                      </a>
                    ) : details.onClick && !isCurrent ? (
                      <button type="button" onClick={details.onClick}>
                        {details.label}
                      </button>
                    ) : (
                      <span aria-current={isCurrent ? "page" : undefined}>
                        {details.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        )}

        {eyebrow && <div className="rg-ui-page-header__eyebrow">{eyebrow}</div>}
        <h1 id={resolvedTitleId} className="rg-ui-page-header__title">
          {title}
        </h1>
        {subtitle && <div className="rg-ui-page-header__subtitle">{subtitle}</div>}
        {children && <div className="rg-ui-page-header__details">{children}</div>}
      </div>

      {(actions || help) && (
        <div className="rg-ui-page-header__tools">
          {help && <div className="rg-ui-page-header__help">{help}</div>}
          {actions && <div className="rg-ui-page-header__actions">{actions}</div>}
        </div>
      )}
    </header>
  );
};

export default PageHeader;
